export interface BatchAllocationEntry {
  batch_id: string;
  batch_no?: string;
  qty: number;
  rate?: number;
  total?: number;
  available_qty?: number;
}

export interface BatchAllocationSum {
  qty: number;
  batch_no: string;
}

export interface BatchAllocationPoolOptions {
  fieldName: string;
  excludeRowIndex?: number;
}

/** Cross-row pool validation applies only when batches come from shared stock (issue mode). */
export const isBatchAllocationIssueMode = (control: {
  lookupName?: string;
}): boolean => Boolean(control.lookupName);

const parseAllocationQty = (value: unknown): number => {
  const n = parseFloat(String(value ?? "0"));
  return Number.isFinite(n) ? n : 0;
};

/** Parse batch allocation array from a submodule row. */
/** Sum line amounts from batch allocation entries (qty × rate, or stored total). */
export const sumBatchAllocationAmount = (
  row: Record<string, any>,
  fieldName: string
): number => {
  return parseRowBatchAllocations(row, fieldName).reduce((sum, alloc) => {
    if (alloc.total !== undefined && Number.isFinite(alloc.total)) {
      return sum + alloc.total;
    }
    const rate = alloc.rate ?? 0;
    return sum + alloc.qty * rate;
  }, 0);
};

export const parseRowBatchAllocations = (
  row: Record<string, any>,
  fieldName: string
): BatchAllocationEntry[] => {
  const raw = row[fieldName] ?? row.batch_allocation;
  if (!raw) return [];

  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item: any) => ({
        batch_id: String(item.batch_id || item.id || ""),
        batch_no: item.batch_no ?? item.batchno ?? "",
        qty: parseAllocationQty(item.qty),
        rate: item.rate !== undefined ? parseAllocationQty(item.rate) : undefined,
        total: item.total !== undefined ? parseAllocationQty(item.total) : undefined,
        available_qty:
          item.available_qty !== undefined
            ? parseAllocationQty(item.available_qty)
            : undefined,
      }))
      .filter((item) => item.batch_id && item.qty > 0);
  } catch {
    return [];
  }
};

/** Sum allocated qty per batch across active submodule rows. */
export const sumBatchAllocationsByBatchId = (
  rows: Record<string, any>[],
  options: BatchAllocationPoolOptions
): Record<string, BatchAllocationSum> => {
  const { fieldName, excludeRowIndex } = options;
  const totals: Record<string, BatchAllocationSum> = {};

  rows.forEach((row, index) => {
    if (row.soft_delete === 1 || row.soft_delete === "1") return;
    if (excludeRowIndex !== undefined && index === excludeRowIndex) return;

    for (const alloc of parseRowBatchAllocations(row, fieldName)) {
      const batchId = alloc.batch_id;
      const existing = totals[batchId];
      totals[batchId] = {
        qty: (existing?.qty ?? 0) + alloc.qty,
        batch_no: alloc.batch_no || existing?.batch_no || batchId,
      };
    }
  });

  return totals;
};

/** Resolve original available qty for a batch from allocation entries across rows. */
export const getBatchAvailableQty = (
  batchId: string,
  rows: Record<string, any>[],
  fieldName: string
): number => {
  let maxAvailable = 0;

  for (const row of rows) {
    if (row.soft_delete === 1 || row.soft_delete === "1") continue;
    for (const alloc of parseRowBatchAllocations(row, fieldName)) {
      if (alloc.batch_id !== batchId) continue;
      if (alloc.available_qty !== undefined && alloc.available_qty > maxAvailable) {
        maxAvailable = alloc.available_qty;
      }
    }
  }

  return maxAvailable;
};

const qtysWithinTolerance = (a: number, b: number): boolean =>
  Math.abs(a - b) < 0.001;

/** Block submit when the same batch is over-allocated across submodule rows. */
export const validateBatchAllocationPools = (
  rows: Record<string, any>[],
  options: BatchAllocationPoolOptions
): string | null => {
  const activeRows = rows.filter(
    (row) => row.soft_delete !== 1 && row.soft_delete !== "1"
  );
  if (activeRows.length === 0) return null;

  const { fieldName } = options;
  const totals = sumBatchAllocationsByBatchId(activeRows, { fieldName });

  for (const [batchId, { qty: totalAlloc, batch_no }] of Object.entries(totals)) {
    if (totalAlloc <= 0) continue;

    const available = getBatchAvailableQty(batchId, activeRows, fieldName);
    if (available <= 0) continue;
    if (qtysWithinTolerance(totalAlloc, available) || totalAlloc < available) {
      continue;
    }

    const label = batch_no || batchId;
    return `Batch ${label}: total allocated (${totalAlloc}) exceeds available (${available})`;
  }

  return null;
};

/** Detect cross-row over-allocation for a single batch in the current row. */
export const getCrossRowBatchConflict = (
  batchId: string,
  currentRowQty: number,
  otherRowsUsage: Record<string, BatchAllocationSum>,
  availableQty: number,
  batchNo?: string
): string | null => {
  const usedElsewhere = otherRowsUsage[batchId]?.qty ?? 0;
  const total = currentRowQty + usedElsewhere;
  if (availableQty <= 0 || total <= availableQty + 0.001) return null;
  const label = batchNo || otherRowsUsage[batchId]?.batch_no || batchId;
  return `Batch ${label} over-allocated across rows (${total}/${availableQty})`;
};
